import { ReactElement, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { className } from "../../../shared/helpers";
import useWindowWidth from "../../../hooks/UseWindowWidth";
import { IComponent } from "../../../services/http/component/models/IComponent";
import Header from "./Header";
import MainIndicator from "./MainIndicator";
import Breadcrumb from "./Breadcrumb";
import ComponentType from "./ComponentType";
import Spinner from "../../../components/Shared/Spinner";
import styles from "./DashboardSideBar.module.scss";

interface DashboardSideBarProps
{
    component: IComponent;
    children?: IComponent[];
    parents?: IComponent[];
    loading: boolean;
}

const DashboardSideBar = (props: DashboardSideBarProps): ReactElement =>
{
    const [showChildren, setShowChildren] = useState(true);
    const [sidebarHidden, setSidebarHidden] = useState(true);
    const history = useHistory();
    const isDesktop = useWindowWidth(992);

    function has(x: number): boolean
    {
        return x !== undefined && x !== null;
    }
    const hasHealthIndicator = has(props.component.healthIndicatorScore) && props.component.healthIndicatorStatusId > 0;
    const hasOfflineIndicator = has(props.component.offlineIndicatorScore) && props.component.offlineIndicatorId > 0;

    useEffect(() =>
    {
        const unlisten = history.listen(() => setSidebarHidden(true));
        return () => unlisten();
    }, [history]);

    useEffect(() => setShowChildren(true), [props.component.parentId]);

    useEffect(() =>
    {
        if (isDesktop && sidebarHidden) setSidebarHidden(false);
        if (!isDesktop && !sidebarHidden)
        {
            document.body.style.setProperty("overflow", "hidden");
        }
        else
        {
            document.body.style.removeProperty("overflow");
        }
    }, [sidebarHidden, isDesktop]);

    return (
        <nav {...className(styles.sidebar, { [styles.isHidden]: sidebarHidden })}>
            <Header
                component={props.component}
                showChildren={showChildren}
                onShowChildren={setShowChildren}
                sidebarHidden={sidebarHidden}
            />
            
            {props.component.id && (hasHealthIndicator || hasOfflineIndicator) && <MainIndicator component={props.component} />}

            {!sidebarHidden && <div {...className(styles.listContainer, "scroll-container")}>
                {props.loading
                    ? <Spinner caption="Loading data..." />
                    : showChildren || !props.component.parentId
                        ? <ComponentType component={props.component} children={props.children || []} />
                        : <Breadcrumb components={props.parents || []} component={props.component}/>
                }
            </div>}

            <div className={styles.togglerContainer}>
                <button
                    {...className("icon-btn", styles.togglerButton, { [styles.isOpened]: sidebarHidden })}
                    onClick={() => setSidebarHidden(!sidebarHidden)}
                >
                    <i className="material-icons">expand_more</i>
                </button>
            </div>
        </nav>
    );
};

export default DashboardSideBar;
